import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Accordion, Flex, Typography } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import { Fragment, useMemo } from 'react';

import { SpecialsTag } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerItems/specialsTag';
import { useSource } from '@jane/monolith/hooks/useSource';
import { getCurrentStoreSpecials } from '@jane/monolith/sources/storeSpecials';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';

export const CartDrawerDiscountTerms = ({
  appliedSpecials,
  freeDeliveryMinimum,
  storeId,
  storeName,
  specialLabel,
  bloomUserExperience,
}: {
  appliedSpecials?: number[];
  bloomUserExperience?: boolean;
  freeDeliveryMinimum?: number | null;
  specialLabel?: string | null;
  storeId?: string | number;
  storeName?: string;
}) => {
  const { data: v1SpecialData } = useSource<{ specials: StoreSpecial[] }>(
    getCurrentStoreSpecials(storeId)
  );
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId: storeId || 0,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1SpecialData?.specials ?? [];
  }, [useV3, v3SpecialsQuery.data, v1SpecialData]);

  const specialsTerms = (appliedSpecials || [])
    .map((specialId) => specials.find((special) => special.id === specialId))
    .filter((special) => !!special);

  const hasSpecialsLabel = specialLabel && specialLabel?.trim().length > 0;

  return (
    <Flex mb={8} flexDirection="column">
      <Accordion>
        <Accordion.Item id="1">
          <Accordion.Heading>
            <Typography as="span" mt={8}>
              Some{' '}
              {/* eslint-disable-next-line react/no-unknown-property, react/forbid-dom-props -- legacy emotion */}
              <span css={{ textDecoration: 'underline' }}>conditions</span> may
              apply to{' '}
              {hasSpecialsLabel ? specialLabel : 'specials and discounts'}
              <Flex ml={4} inline alignItems="center" as="span">
                <Accordion.Indicator iconSize="sm" />
              </Flex>
            </Typography>
          </Accordion.Heading>
          <Accordion.Content destroyOnClose>
            <Typography mt={8}>
              {hasSpecialsLabel ? specialLabel : 'Promotional discounts'} are
              only available while supplies last and are subject to any
              restrictions associated with the{' '}
              {hasSpecialsLabel ? specialLabel : 'promotion'}. Orders must be
              submitted and picked up/delivered within the timeframe that the
              promotion is active. Any{' '}
              {hasSpecialsLabel ? specialLabel : 'discounts'} will be applied by
              the dispensary {storeName && `(${storeName})`} at the time of
              payment, and the dispensary retains ultimate authority for
              verifying and applying the{' '}
              {hasSpecialsLabel ? specialLabel : 'discount'}.
            </Typography>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>

      {specialsTerms.length > 0 && (
        <Flex flexDirection="column" mt={8}>
          <Typography variant="body-semibold">
            {hasSpecialsLabel ? specialLabel : 'Promotional discounts'}
          </Typography>
          <Typography mb={8}>Best available discount is applied.</Typography>
          {specialsTerms.map(({ title, terms }, i) => {
            return (
              <Fragment key={`title-${i}`}>
                {title && (
                  <SpecialsTag
                    title={title}
                    bloomUserExperience={bloomUserExperience}
                  />
                )}
                {terms && (
                  <Typography variant="small-body-regular">{terms}</Typography>
                )}
              </Fragment>
            );
          })}
        </Flex>
      )}

      {!!freeDeliveryMinimum && (
        <Flex flexDirection="column" mt={8}>
          <SpecialsTag
            title={`Free delivery if you spend ${formatCurrency(
              freeDeliveryMinimum
            )}`}
          />
        </Flex>
      )}
    </Flex>
  );
};
